/**
 * Records the EMR page's own DOM for session replay.
 *
 * This runs in the content script, i.e. in the EMR's document — the one place
 * that can see the chart. The recorder in the Comprehend app cannot: in the
 * extension the app is a cross-origin iframe, which is exactly why this file
 * exists. Injector bugs are bugs about what the EMR's DOM looked like, so a
 * replay without this side shows the panel reacting to something invisible.
 *
 * Events are buffered here and PULLED by the panel every few seconds
 * (GET_EMR_REPLAY_EVENTS -> api/replay/emrDomPump.js), which is what keeps this
 * file free of any knowledge of replay ids, uploads or which surface is asking.
 *
 * Unmasked, by design — see the PHI note in api/replay/sidecar.js. The controls
 * are whether a replay runs at all (server), who can watch one (dev + 2FA), and
 * retention.
 */
import { record } from 'rrweb';

/**
 * Re-snapshot on demand, with a long backstop.
 *
 * A drained batch is incremental: it only replays on top of the full snapshot in
 * an earlier batch, so a lost batch — a failed chunk upload, a buffer that
 * dropped events, a panel that reopened while this recorder was still running —
 * leaves everything after it unreplayable until the next full snapshot. The
 * panel knows when each of those happened and asks for one through the drain
 * (requestEmrReplayCheckout), so a snapshot is taken when it is needed rather
 * than on a clock. That matters more than it sounds: a full snapshot of a
 * Prompt chart measured ~29 MB, and taking one every minute was 97% of
 * everything this recorder serialised on the clinician's page. The periodic
 * checkout stays only as a backstop for a loss nothing noticed.
 */
const CHECKOUT_BACKSTOP_MS = 10 * 60_000;
const RECORDING_IDLE_MS = 15_000;

/**
 * Hard cap on the buffer.
 *
 * The panel normally drains every few seconds, but it might never come: no
 * replay is running, the side panel is closed, the user is on an EMR page in a
 * tab Comprehend was never opened in. Recording into an unbounded array for an
 * eight-hour clinic day is not an option, so old events are dropped rather than
 * grown. Dropping the OLDEST keeps the recent window, which is the one a bug
 * report is about.
 */
const MAX_BUFFERED_EVENTS = 4000;

/**
 * The same cap, in bytes.
 *
 * A count is the wrong unit here: a single EMR full snapshot with its
 * stylesheets inlined measures ~3.8 MB, and a mutation that re-adds a <style>
 * node ~1 MB, so 4000 events can be several gigabytes while the count gate sits
 * idle.
 *
 * Dropping the oldest is safe HERE, unlike in the panel's upload buffer, which
 * stops instead (see MAX_BUFFER_BYTES in api/replay/recorder.js). The drained
 * meta reports the drop, and the panel answers it by asking for a fresh
 * snapshot on the next drain, so a hole costs at most one drain interval.
 *
 * The number still has to clear one opening snapshot with room to spare. A Cora
 * Sapphire chart's first snapshot measured 25 MB with its stylesheets inlined,
 * and a ceiling below that would evict the snapshot the moment the next
 * mutation arrived — leaving every drained batch unreplayable until the next
 * checkout.
 */
const MAX_BUFFERED_BYTES = 48 * 1024 * 1024;

/** Assumed cost of one serialised node, excluding its text and stylesheets. */
const NODE_BYTES = 120;

/**
 * Size of one event, near enough, without serialising it.
 *
 * Only `_cssText` and text content are counted because on these pages they ARE
 * the size: inlined stylesheets measure 95-98% of both full snapshots and
 * mutations. Reading string lengths is a few thousand O(1) property reads,
 * where a JSON.stringify of every event would add real main-thread work to a
 * customer's EMR — the one cost this file exists to avoid.
 */
function estimateBytes(event: any): number {
  let total = 256; // envelope and small events, none of which move the needle
  const node = (n: any) => {
    if (!n || typeof n !== 'object') return;
    // A serialised node's own weight: id, type, tagName and its attributes.
    // Rough, but it is the term that makes a mutation carrying ten thousand
    // nodes cost more than one carrying ten — which counting only _cssText
    // could not see, and which is exactly the shape an EMR grid re-render has.
    total += NODE_BYTES;
    const attributes = n.attributes;
    if (attributes && typeof attributes === 'object') {
      const css = attributes._cssText;
      if (typeof css === 'string') {
        total += css.length;
      }
    }
    if (typeof n.textContent === 'string') total += n.textContent.length;
    if (Array.isArray(n.childNodes)) n.childNodes.forEach(node);
  };
  const data = event && event.data;
  if (!data) return total;
  if (event.type === 2) node(data.node);
  else if (event.type === 3 && data.source === 0) {
    if (Array.isArray(data.adds)) data.adds.forEach((add: any) => node(add && add.node));
    // rrweb inlines a whole sheet here too, when a <link> finishes loading.
    if (Array.isArray(data.attributes)) data.attributes.forEach((m: any) => node(m));
    // And a characterData rewrite of a <style>'s text node carries the whole sheet.
    if (Array.isArray(data.texts)) {
      data.texts.forEach((t: any) => {
        if (t && typeof t.value === 'string') total += t.value.length;
      });
    }
  }
  return total;
}

let buffer: any[] = [];
let bufferSizes: number[] = [];
let bufferBytes = 0;

let stopFn: (() => void) | undefined;
let stopTimer: ReturnType<typeof setTimeout> | undefined;
let started = false;
let recordingError: string | null = null;
let droppedEvents = 0;

/**
 * Mutation-storm guard.
 *
 * EMR schedules and flowsheets rebuild thousands of nodes at once, and rrweb
 * serialises every mutation synchronously on the main thread. If the recorder
 * starts costing the clinician real time, stop recording — a slow EMR is a
 * worse bug than a missing replay. This is the guard the old outerHTML
 * snapshotter did not have, which is why that one broke customer pages.
 */
const EMIT_BUDGET_LIMIT_MS = 400;
const EMIT_BUDGET_WINDOW_MS = 10_000;
let emitBudgetMs = 0;
let emitBudgetWindowStart = Date.now();

function onEvent(event: any, isCheckout?: boolean) {
  const startedAt = performance.now();

  // A checkout means this event is a fresh full snapshot, so everything before
  // it is redundant — provided the buffer has not already been drained past it.
  // rrweb emits a checkout as TWO flagged events, Meta (type 4) then
  // FullSnapshot, so clear on the Meta only: clearing on both evicts the Meta
  // the snapshot needs (the Replayer sizes and shows its iframe from it).
  if (isCheckout && event.type === 4) {
    buffer = [];
    bufferSizes = [];
    bufferBytes = 0;
  }

  buffer.push(event);
  const eventBytes = estimateBytes(event);
  bufferSizes.push(eventBytes);
  bufferBytes += eventBytes;

  // Drop the OLDEST on either bound, for the reason in MAX_BUFFERED_EVENTS: the
  // recent window is the one a bug report is about.
  while (
    buffer.length > 1 &&
    (buffer.length > MAX_BUFFERED_EVENTS || bufferBytes > MAX_BUFFERED_BYTES)
  ) {
    buffer.shift();
    bufferBytes -= bufferSizes.shift() as number;
    droppedEvents += 1;
  }

  emitBudgetMs += performance.now() - startedAt;
  const now = Date.now();
  if (now - emitBudgetWindowStart > EMIT_BUDGET_WINDOW_MS) {
    emitBudgetWindowStart = now;
    emitBudgetMs = 0;
  } else if (emitBudgetMs > EMIT_BUDGET_LIMIT_MS) {
    recordingError = 'auto-disabled: recorder exceeded its main-thread budget';
    console.log('[cpt-replay] EMR recording auto-disabled on this page (mutation storm guard)');
    stopEmrReplayRecording();
  }
}

export function isPromptPage(hostname: string = window.location.hostname): boolean {
  return /(^|\.)promptemr\.com$/.test(hostname);
}

/**
 * Start recording, or keep an existing recording alive. Returns true only when
 * this call started it — record() takes its own opening snapshot, so the
 * caller can tell a fresh stream from one that may still be on a previous
 * replay's baseline.
 */
export function startEmrReplayRecording(): boolean {
  if (recordingError) return false;
  if (stopTimer) clearTimeout(stopTimer);
  stopTimer = setTimeout(stopEmrReplayRecording, RECORDING_IDLE_MS);
  if (started) return false;
  started = true;
  recordingError = null;
  try {
    stopFn = record({
      emit: onEvent,
      checkoutEveryNms: CHECKOUT_BACKSTOP_MS,
      // Inline styles so a replay renders without fetching the EMR's
      // stylesheets, which are behind the customer's session and would 401 at
      // replay time. Fonts are left alone: they are large and cosmetic.
      // Except Prompt: it links ~270k CSS rules (~25 MB), and reading them makes
      // Chrome build a CSSOM object per rule for the life of the page — the bulk
      // of Prompt's extra memory with replay on. Its /assets/*.css load without
      // a login, so the viewer fetches them by URL instead.
      inlineStylesheet: !isPromptPage(),
      recordCanvas: false,
      maskAllInputs: false,
      // rrweb's conservative set: comments, <script> elements, favicons, head
      // whitespace and the social/robots/http-equiv meta tags. Nothing it drops
      // is rendered in a replay (the player iframe has no allow-scripts), and
      // script BODIES were never recorded anyway, so this saves node overhead
      // and head metadata rather than text.
      slimDOMOptions: true,
    });
    if (!stopFn) throw new Error('rrweb record() did not start');
    return true;
  } catch (e) {
    recordingError = String(e);
    console.log('[cpt-replay] EMR rrweb record() failed', e);
    if (stopTimer) clearTimeout(stopTimer);
    stopTimer = undefined;
    started = false;
    return false;
  }
}

/**
 * Take a full snapshot now, so the next drained batch stands on its own.
 * Asked for through the drain when the panel knows the batches before it are
 * unusable to the viewer — see the note on CHECKOUT_BACKSTOP_MS.
 */
export function requestEmrReplayCheckout(): boolean {
  if (!started || !stopFn) return false;
  try {
    record.takeFullSnapshot(true);
    return true;
  } catch (e) {
    console.log('[cpt-replay] EMR snapshot request failed', e);
    return false;
  }
}

export function stopEmrReplayRecording() {
  if (stopTimer) clearTimeout(stopTimer);
  stopTimer = undefined;
  try {
    if (stopFn) stopFn();
  } catch (e) {
    /* rrweb throws if it was never fully started */
  }
  stopFn = undefined;
  started = false;
}

/**
 * Hand the buffered events to the caller and clear.
 *
 * Clearing on read is what makes each batch deliver exactly once; the pump
 * serialises its calls so two drains cannot interleave and duplicate.
 */
export function drainEmrReplayEvents() {
  const events = buffer;
  buffer = [];
  bufferSizes = [];
  bufferBytes = 0;
  const dropped = droppedEvents;
  droppedEvents = 0;
  return {
    events,
    meta: {
      url: window.location.href,
      title: document.title,
      recording: started,
      recordingError,
      // Surfaced so a replay that looks like it jumps can be explained rather
      // than debugged: it means the panel stopped draining, not that the EMR did
      // something strange.
      droppedEvents: dropped,
    },
  };
}
